// packages/ui-kit/src/primitives/Select.tsx
// C3F v18 — reference: design/system/components/forms/Select.{jsx,prompt.md}
// Native <select> on purpose: it works with gloves and with screen readers.
import { type SelectHTMLAttributes, type ReactNode, forwardRef } from "react";

type Size = "sm" | "md" | "lg";
type Option = string | { value: string; label: string };

interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  size?: Size;
  error?: boolean;
  /** Convenience list; pass `children` instead for grouped/custom options. */
  options?: ReadonlyArray<Option>;
  children?: ReactNode;
}

// DS heights: sm 38 · md 44 · lg 52; 44px touch floor on phones.
// Right padding reserves the chevron gutter (DS: 38px).
const boxCls: Record<Size, string> = {
  sm: "min-h-touch sm:min-h-[38px] pl-3 pr-[38px]",
  md: "min-h-touch pl-3.5 pr-[38px]",
  lg: "min-h-[52px] pl-3.5 pr-[38px]",
};

// >=16px body on phones prevents iOS focus-zoom; DS desktop body is 14px.
const fontCls: Record<Size, string> = {
  sm: "text-base sm:text-sm",
  md: "text-base sm:text-sm",
  lg: "text-base",
};

export const Select = forwardRef<HTMLSelectElement, Props>(function Select(
  { size = "md", error = false, options, className = "", children, ...rest },
  ref,
) {
  // Fields use --border-control (WCAG 1.4.11); hover never darkens the border.
  const borderCls = error
    ? "border-danger-strong"
    : "border-border-control focus-visible:border-border-focus";
  return (
    <span className="relative block w-full">
      <select
        ref={ref}
        className={`block w-full cursor-pointer appearance-none rounded-md border bg-elevated font-sans text-fg-default outline-none transition-[border-color,box-shadow] duration-fast focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-45 ${borderCls} ${boxCls[size]} ${fontCls[size]} ${className}`}
        {...rest}
      >
        {children ??
          options?.map((o) => {
            const value = typeof o === "string" ? o : o.value;
            const label = typeof o === "string" ? o : o.label;
            return (
              <option key={value} value={value}>
                {label}
              </option>
            );
          })}
      </select>
      {/* System-drawn chevron (CSS borders, per DS reference — no SVG). */}
      <span
        aria-hidden
        className="pointer-events-none absolute right-3.5 top-1/2 h-[7px] w-[7px] -translate-y-[60%] rotate-45 border-b-[1.5px] border-r-[1.5px] border-fg-muted"
      />
    </span>
  );
});
